import React, { FunctionComponent, ReactElement } from 'react';
import { DealerLaunchStepProps } from '../types';
import { Dealer } from '../../../types/dealerTypes';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { DealerLaunchFormCardLayout } from '../DealerLaunchFormCardLayout';
import styles from '../styles.module.scss';
import checkStar from '../../../assets/check-green-star.png';
import { PreviewGuide } from '../../QuickGuide/QuickGuide';
import { LendingStatus } from '../../../utils/constants';
import { getLendingStatus } from '../../../utils/lending';
import { useTypedSelector } from '../../../redux/store';

const modalP1 = `Upstart Auto Retail offers real-time financial approvals through
approved lending partners so that your customers get easy access to
the best finance offers faster.`;
const modalP2 = ` We have enabled real-time financial approvals to create the best car
buying experience possible.`;
const modalP3 = `There are more steps you must complete in your F&I system later. Don't
worry -- you don't need to do this right now. We'll walk you through
the process later.`;

const nextParagraph =
  "Next, we're going to ask you questions about business and contact info to get started with Upstart Auto Retail Lending.";

export const DealerLaunchLendingPromo: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  prevStep,
  nextStep,
}) => {
  const form = useLaunchpadForm<Dealer>({ defaultValues: dealer });
  const globalConfigurationStore = useTypedSelector(
    (state) => state.globalConfigurations,
  );
  const { globalConfigurations } = globalConfigurationStore;
  const lendingStatus = getLendingStatus(dealer, globalConfigurations);
  const title =
    lendingStatus === LendingStatus.Enabled
      ? "Congrats! You're qualified for Upstart Auto Retail Lending"
      : 'Get Started - Upstart Auto Retail Lending';

  interface DealerWin {
    what: string;
    how: string;
    info?: ReactElement;
  }

  const realTimeInfoLink: ReactElement = (
    <span className={styles.lendingPreview}>
      <span>ENABLED BY DEFAULT</span>
      <PreviewGuide
        action={<span className={styles.lendingPreviewText}>Learn More</span>}
        footerClose
        hideIcon
      >
        <h1 className={styles.previewTitle}>
          Real-Time Approvals Enabled by Default
        </h1>
        <h2 className={styles.previewSubTitle}>
          Why are Real-Time Approvals Valuable?
        </h2>
        <div className={styles.previewBody}>
          <p>{modalP1}</p>
          <p>{modalP2}</p>
          <p>{modalP3}</p>
        </div>
      </PreviewGuide>
    </span>
  );

  const wins: Array<DealerWin> = [
    { what: 'Competitive reserve rates', how: 'Up to 3%' },
    {
      what: 'More advance for F&I products',
      how: 'Up to 185% LTV with no max backend',
    },
    {
      what: 'Real-Time Approvals',
      how: 'With next day funding',
      info:
        lendingStatus !== LendingStatus.Disabled ? realTimeInfoLink : undefined,
    },
    {
      what: 'Up to 84-month terms',
      how: 'Customize terms, APRs, discounts, down payments and more',
    },
  ];

  return (
    <DealerLaunchFormCardLayout
      form={form}
      title={title}
      cardTitle="What is Upstart Auto Retail Lending?"
      prevStep={prevStep}
      onFormSubmit={nextStep}
      className={styles.dealerLending}
    >
      <div className={styles.desc}>
        <p>
          Turn every deal into a win-win scenario with{' '}
          <b>AI-Powered Financing</b> from Upstart Auto Retail.
        </p>
      </div>
      <div>
        <div className={styles.dealerWin}>
          <h2 className={styles.winHeader}>A win for your dealership:</h2>
          <ul className={styles.winList}>
            {wins.map((win, idx) => {
              return (
                /* eslint-disable-next-line react/no-array-index-key */
                <li key={idx} className={styles.winListItem}>
                  <img
                    className={styles.listIcon}
                    src={checkStar}
                    alt="'check'"
                  />
                  <div>
                    {win.what} {win.info}
                    <span className={styles.winHow}>{win.how}</span>
                  </div>
                </li>
              );
            })}
          </ul>
          <p>{nextParagraph}</p>
        </div>
      </div>
    </DealerLaunchFormCardLayout>
  );
};
